import { useEffect, useRef, useState } from 'react';
import { Settings, UserPlus, UserMinus, Archive, Trash2, Camera, Shield, LogOut, Bell, BellOff } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import usePotvrzeni from '../ui/usePotvrzeni';
import Avatar from '../../lib/avatar';
import { SECTION_COLOR, SECTION_BG, hlavicky } from './komunikace';

const IKONY = ['💬', '🏠', '🍕', '🔥', '⭐', '🎯', '📢', '🎉', '💼', '🛠️', '📋', '🎓', '🚀', '💡', '🏆', '❤️'];

/**
 * Nastavení skupiny a sdělení — `ModalShell` (norma: nastavení = modal).
 * Dřív blok rozbalený uvnitř chatu nad zprávami, s `window.confirm`
 * u archivace a smazání. Správce vidí všechno, člen jen „Opustit".
 */
export default function NastaveniKonverzace({ konverzaceId, detail, user, isDark, onClose, onZmena, onOdejit }) {
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);
    const [nazev, setNazev] = useState(detail?.nazev || '');
    const [prezdivka, setPrezdivka] = useState(detail?.prezdivka || '');
    const [hledani, setHledani] = useState('');
    const [vysledky, setVysledky] = useState([]);
    const [chyba, setChyba] = useState(null);
    const [nahravam, setNahravam] = useState(false);
    const souborRef = useRef(null);

    const jeSoukroma = detail?.typ === 'soukromy';
    const jeKanal = detail?.typ === 'kanal';
    const jsemSpravce = !jeSoukroma && detail?.ucastnici?.find(u => u.id === user?.id)?.role === 'admin';
    const jeSdeleni = detail?.typ === 'sdeleni';
    // Členy kanálu určuje pravidlo — ručně se nepřidávají, neodebírají ani neodchází
    const spravujeCleny = jsemSpravce && !jeKanal;

    useEffect(() => {
        if (!hledani.trim()) { setVysledky([]); return; }
        const t = setTimeout(() => {
            const kontext = jeSdeleni ? '&context=sdeleni' : '';
            fetch(`/api/chat/uzivatele?q=${encodeURIComponent(hledani)}${kontext}`).then(r => (r.ok ? r.json() : []))
                .then(d => {
                    const uz = new Set((detail?.ucastnici || []).map(u => u.id));
                    setVysledky((Array.isArray(d) ? d : []).filter(u => !uz.has(u.id)));
                }).catch(() => setVysledky([]));
        }, 250);
        return () => clearTimeout(t);
    }, [hledani, jeSdeleni, detail]);

    const zavolej = async (url, metoda, telo = null) => {
        setChyba(null);
        try {
            const res = await fetch(url, { method: metoda, headers: hlavicky(!!telo), body: telo ? JSON.stringify(telo) : undefined });
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                setChyba(e.error || e.message || 'Nepodařilo se uložit.');
                return false;
            }
            return true;
        } catch {
            setChyba('Chyba sítě — zkuste to znovu.');
            return false;
        }
    };

    const uloz = async (zmena) => { if (await zavolej(`/api/chat/konverzace/${konverzaceId}`, 'PATCH', zmena)) onZmena(); };

    const pridat = async (u) => {
        if (await zavolej(`/api/chat/konverzace/${konverzaceId}/ucastnici`, 'POST', { uzivatel_id: u.id })) {
            setHledani(''); setVysledky([]); onZmena();
        }
    };
    const odebrat = async (u) => {
        if (!(await potvrdit(`Odebrat ${u.jmeno}?`, { text: 'Přestane dostávat nové zprávy této konverzace.', potvrdit: 'Odebrat' }))) return;
        if (await zavolej(`/api/chat/konverzace/${konverzaceId}/ucastnici/${u.id}`, 'DELETE')) onZmena();
    };
    const prepniSpravce = async (u) => {
        if (await zavolej(`/api/chat/konverzace/${konverzaceId}/ucastnici/${u.id}`, 'PATCH', { role: u.role === 'admin' ? 'clen' : 'admin' })) onZmena();
    };
    const opustit = async () => {
        if (!(await potvrdit('Opustit konverzaci?', { text: 'Zprávy z ní vám přestanou chodit. Vrátit vás může správce.', potvrdit: 'Opustit' }))) return;
        if (await zavolej(`/api/chat/konverzace/${konverzaceId}/ucastnici/${user.id}`, 'DELETE')) onOdejit();
    };
    const archivovat = async () => {
        if (!(await potvrdit('Archivovat konverzaci?', { text: 'Zmizí ze seznamu všem účastníkům. Zprávy zůstanou uložené.', potvrdit: 'Archivovat', nebezpecne: false }))) return;
        if (await zavolej(`/api/chat/konverzace/${konverzaceId}`, 'PATCH', { archivovano: true })) onOdejit();
    };
    const smazat = async () => {
        if (!(await potvrdit('Smazat celou konverzaci?', { text: 'Smaže se pro všechny účastníky. Tahle akce je nevratná.', potvrdit: 'Smazat' }))) return;
        if (await zavolej(`/api/chat/konverzace/${konverzaceId}`, 'DELETE')) onOdejit();
    };

    const prepniZtlumeni = async () => {
        if (await zavolej(`/api/chat/konverzace/${konverzaceId}/ztlumit`, 'PATCH', { ztlumeno: !detail?.ztlumeno })) onZmena();
    };

    const nahratObrazek = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setNahravam(true);
        const fd = new FormData();
        fd.append('avatar', f);
        try {
            const res = await fetch(`/api/chat/konverzace/${konverzaceId}/avatar`, { method: 'POST', headers: hlavicky(false), body: fd });
            if (res.ok) onZmena(); else setChyba('Obrázek se nepodařilo nahrát.');
        } catch { setChyba('Obrázek se nepodařilo nahrát.'); }
        setNahravam(false);
        e.target.value = '';
    };

    const hrana = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const pole = { flex: 1, height: 40, borderRadius: 8, padding: '0 12px', outline: 'none', fontSize: 14, background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${hrana}`, color: 'var(--text-primary)', minWidth: 0 };
    const tlac = { height: 40, padding: '0 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: `1px solid ${hrana}`, color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: 6 };
    const popisek = { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '16px 0 6px' };

    return (
        <>
        <ModalShell onClose={onClose} isDark={isDark} nadpis={jsemSpravce ? 'Nastavení konverzace' : jeSoukroma ? 'Upozornění' : 'Konverzace'} ikona={Settings} barva={SECTION_COLOR} sirka={500}
            paticka={chyba ? <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p> : null}>
            <div style={{ padding: '0 16px 16px' }}>
                <p style={popisek}>Upozornění</p>
                <button type="button" onClick={prepniZtlumeni} className="w-full flex items-center text-left"
                    style={{ ...tlac, width: '100%', height: 'auto', minHeight: 48, padding: '8px 12px', gap: 10 }}>
                    {detail?.ztlumeno ? <BellOff size={16} style={{ color: SECTION_COLOR }} /> : <Bell size={16} />}
                    <span className="flex-1">
                        <span style={{ display: 'block' }}>{detail?.ztlumeno ? 'Ztlumeno' : 'Upozornění zapnutá'}</span>
                        <span style={{ display: 'block', fontSize: 11.5, fontWeight: 400, color: 'var(--text-muted)' }}>
                            {detail?.ztlumeno ? 'Push jen při zmínce @jméno, do odznaku se nepočítá. Klepnutím zapnout.' : 'Klepnutím ztlumit — zmínky @jméno projdou dál.'}
                        </span>
                    </span>
                </button>

                {jeKanal && detail?.pravidlo && (
                    <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 12 }}>
                        Kanál — členové se řídí pravidlem ({detail.pravidlo}), ručně se nepřidávají ani neodebírají.
                    </p>
                )}

                {jsemSpravce && (
                    <>
                        <p style={popisek}>Název</p>
                        <div className="flex gap-2">
                            <input value={nazev} onChange={e => setNazev(e.target.value)} style={pole} />
                            <button type="button" style={tlac} disabled={!nazev.trim() || nazev === detail?.nazev} onClick={() => uloz({ nazev: nazev.trim() })}>Uložit</button>
                        </div>

                        {jeSdeleni && (
                            <>
                                <p style={popisek}>Podpis autora</p>
                                <div className="flex gap-2">
                                    <input value={prezdivka} onChange={e => setPrezdivka(e.target.value)} placeholder="Např. Vedení" style={pole} />
                                    <button type="button" style={tlac} onClick={() => uloz({ prezdivka: prezdivka.trim() || null })}>Uložit</button>
                                </div>
                            </>
                        )}

                        <p style={popisek}>Ikona</p>
                        <div className="flex flex-wrap gap-1 items-center">
                            {IKONY.map(i => (
                                <button key={i} type="button" onClick={() => uloz({ ikona: i })}
                                    style={{ width: 36, height: 36, borderRadius: 8, fontSize: 18, cursor: 'pointer', background: detail?.ikona === i && !detail?.avatar_url ? SECTION_BG : 'transparent', border: `1px solid ${detail?.ikona === i && !detail?.avatar_url ? SECTION_COLOR : 'transparent'}` }}>
                                    {i}
                                </button>
                            ))}
                            <button type="button" style={{ ...tlac, height: 36 }} onClick={() => souborRef.current?.click()} disabled={nahravam}>
                                <Camera size={14} /> {nahravam ? 'Nahrávám…' : 'Vlastní obrázek'}
                            </button>
                            <input ref={souborRef} type="file" accept="image/*" className="hidden" onChange={nahratObrazek} />
                        </div>
                    </>
                )}

                {!jeSoukroma && <p style={popisek}>Členové · {detail?.ucastnici?.length || 0}</p>}
                <div className="flex flex-col">
                    {(jeSoukroma ? [] : (detail?.ucastnici || [])).map(u => (
                        <div key={u.id} className="flex items-center" style={{ gap: 10, minHeight: 46, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-subtle)'}` }}>
                            <Avatar url={u.avatar_url} jmeno={u.jmeno} size={28} isDark={isDark} />
                            <span className="flex-1 min-w-0 truncate text-sm" style={{ color: 'var(--text-primary)' }}>
                                {u.jmeno}{u.id === user?.id ? ' (vy)' : ''}
                            </span>
                            {u.role === 'admin' && <span className="text-[11px] font-semibold" style={{ color: SECTION_COLOR }}>správce</span>}
                            {spravujeCleny && u.id !== user?.id && (
                                <>
                                    <button type="button" title={u.role === 'admin' ? 'Odebrat správce' : 'Udělat správcem'} onClick={() => prepniSpravce(u)}
                                        style={{ ...tlac, height: 32, width: 32, padding: 0, justifyContent: 'center', color: u.role === 'admin' ? SECTION_COLOR : 'var(--text-secondary)' }}>
                                        <Shield size={14} />
                                    </button>
                                    <button type="button" title="Odebrat z konverzace" onClick={() => odebrat(u)}
                                        style={{ ...tlac, height: 32, width: 32, padding: 0, justifyContent: 'center', color: 'var(--text-secondary)' }}>
                                        <UserMinus size={14} />
                                    </button>
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {spravujeCleny && (
                    <>
                        <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
                            <UserPlus size={16} style={{ color: 'var(--text-muted)' }} />
                            <input value={hledani} onChange={e => setHledani(e.target.value)} placeholder="Přidat člena…" style={pole} />
                        </div>
                        {vysledky.map(u => (
                            <button key={u.id} type="button" onClick={() => pridat(u)} className="w-full flex items-center text-left rounded-lg"
                                style={{ gap: 10, padding: '8px 10px', minHeight: 44, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                <Avatar url={u.avatar_url} jmeno={u.jmeno} size={26} isDark={isDark} />
                                <span className="text-sm flex-1">{u.jmeno}</span>
                                <UserPlus size={14} style={{ color: SECTION_COLOR }} />
                            </button>
                        ))}
                    </>
                )}

                {!jeSoukroma && !jeKanal && (
                    <div className="flex flex-wrap gap-2" style={{ marginTop: 20 }}>
                        <button type="button" style={tlac} onClick={opustit}><LogOut size={14} /> Opustit</button>
                        {jsemSpravce && <button type="button" style={tlac} onClick={archivovat}><Archive size={14} /> Archivovat</button>}
                        {jsemSpravce && <button type="button" style={{ ...tlac, color: '#EF4444' }} onClick={smazat}><Trash2 size={14} /> Smazat</button>}
                    </div>
                )}
            </div>
        </ModalShell>
        {potvrzeni}
        </>
    );
}
